import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Play, ImageIcon, Film } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { extractEmbedUrl } from "@/lib/mediaUrl";

function Card({ item, onOpen, idx }) {
    const isVideo = item.kind === "video";
    const cover = item.thumbnail_url || (isVideo ? "" : item.url);
    return (
        <button
            type="button"
            onClick={() => onOpen(item)}
            className="group relative aspect-[4/3] rounded-2xl overflow-hidden bg-paper-alt border border-gray-100 transition-all duration-300 hover:shadow-brand hover:-translate-y-1 animate-fade-up text-left"
            style={{ animationDelay: `${(idx % 12) * 30}ms` }}
            data-testid={`media-card-${item.id}`}
        >
            {cover ? (
                <img
                    src={cover}
                    alt={item.title || (isVideo ? "Video" : "Photo")}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
            ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-ink-900/80 text-white">
                    <Film className="h-10 w-10" />
                </div>
            )}
            {isVideo && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors group-hover:bg-black/40">
                    <span className="h-12 w-12 rounded-full bg-white/90 text-brand-700 flex items-center justify-center shadow-lg">
                        <Play className="h-5 w-5 ml-0.5" strokeWidth={2.4} fill="currentColor" />
                    </span>
                </span>
            )}
            {item.title && (
                <span className="absolute bottom-0 left-0 right-0 px-3 py-2 text-xs font-medium text-white bg-gradient-to-t from-black/70 to-transparent">
                    {item.title}
                </span>
            )}
        </button>
    );
}

function Lightbox({ item, onClose }) {
    if (!item) return null;
    const isVideo = item.kind === "video";
    const src = isVideo ? extractEmbedUrl(item.url) : item.url;
    return (
        <div
            className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center p-4 animate-fade-up"
            onClick={onClose}
            data-testid="media-lightbox"
        >
            <button
                type="button"
                onClick={onClose}
                className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-xl"
                aria-label="Close"
            >
                ×
            </button>
            <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
                {isVideo ? (
                    /\.(mp4|webm|ogg)(\?.*)?$/i.test(src) ? (
                        <video src={src} controls autoPlay className="w-full max-h-[85vh] rounded-2xl" />
                    ) : (
                        <div className="relative w-full aspect-video rounded-2xl overflow-hidden">
                            <iframe
                                src={src}
                                title={item.title || "Video"}
                                allow="autoplay; encrypted-media; picture-in-picture"
                                allowFullScreen
                                className="absolute inset-0 w-full h-full"
                            />
                        </div>
                    )
                ) : (
                    <img
                        src={item.url}
                        alt={item.title || "Photo"}
                        className="w-full max-h-[85vh] object-contain rounded-2xl"
                    />
                )}
            </div>
        </div>
    );
}

export default function MediaPage({ onOpenAuth }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);
    const [params, setParams] = useSearchParams();
    const filter = params.get("kind") || "all";

    useEffect(() => {
        api.get("/media-gallery")
            .then(({ data }) => setItems(data || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, []);

    const setFilter = (k) => {
        if (k === "all") {
            params.delete("kind");
            setParams(params);
        } else {
            params.set("kind", k);
            setParams(params);
        }
    };

    const filtered = useMemo(
        () => (filter === "all" ? items : items.filter((m) => m.kind === filter)),
        [filter, items],
    );

    const FILTERS = [
        { key: "all", label: "All", Icon: null },
        { key: "image", label: "Photos", Icon: ImageIcon },
        { key: "video", label: "Videos", Icon: Film },
    ];

    return (
        <div className="min-h-screen bg-paper" data-testid="media-page">
            <Navbar onOpenAuth={onOpenAuth} />
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
                <div className="text-center max-w-2xl mx-auto">
                    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-50 text-brand-700 text-xs font-semibold tracking-wide uppercase">
                        Gallery
                    </span>
                    <h1 className="mt-5 text-4xl md:text-5xl font-heading font-semibold tracking-tight text-ink-900">
                        Media gallery
                    </h1>
                    <p className="mt-3 text-base md:text-lg text-ink-700">
                        Photos and videos from our classes, workshops, and student community.
                    </p>
                </div>

                <div className="mt-8 flex flex-wrap justify-center gap-3" role="tablist">
                    {FILTERS.map(({ key, label, Icon }) => {
                        const isActive = filter === key;
                        return (
                            <button
                                key={key}
                                onClick={() => setFilter(key)}
                                role="tab"
                                aria-selected={isActive}
                                className={`filter-pill border inline-flex items-center gap-1.5 ${
                                    isActive
                                        ? "bg-brand-500 text-white border-brand-500 shadow-cta"
                                        : "bg-white text-ink-700 border-gray-200 hover:border-brand-300 hover:text-brand-700"
                                }`}
                                data-testid={`media-filter-${key}`}
                            >
                                {Icon && <Icon className="h-3.5 w-3.5" />}
                                {label}
                            </button>
                        );
                    })}
                </div>

                <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                    {loading
                        ? [0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                              <div key={i} className="aspect-[4/3] rounded-2xl bg-gray-100 animate-pulse" />
                          ))
                        : filtered.map((m, idx) => (
                              <Card key={m.id} item={m} idx={idx} onOpen={setActive} />
                          ))}
                </div>

                {!loading && filtered.length === 0 && (
                    <div className="text-center py-20 text-ink-700">
                        No {filter === "all" ? "media" : filter + "s"} yet.{" "}
                        <Link to="/" className="text-brand-600 hover:underline">
                            Back to home
                        </Link>
                    </div>
                )}
            </main>
            <Footer />
            <Lightbox item={active} onClose={() => setActive(null)} />
        </div>
    );
}
